import React from "react";
import { Link } from "react-router-dom";
import client from "@culturehq/client";
import { Icon, Loader, Pagination, Panel, Table, Tooltip, Warning } from "@culturehq/components";

import { useProfile } from "app/context";
import { useFetch, usePagination } from "app/hooks";
import { EventLink } from "app/links";
import { t, Trans } from "app/utils";

import StarRatingAverage from "components/common/StarRatingAverage";
import { Time } from "components/common/Time";
import PageHeader from "components/display/PageHeader";

import ExportEvents from "./ExportEvents";

const EventRowRating = ({ eventAnalytics }) => {
  const { profile } = useProfile();
  const { event } = eventAnalytics;

  if (event.hostOnlySurvey && event.host.id !== profile.id) {
    return (
      <Tooltip tip={t("The results of this survey are only visible to the host.")}>
        <Icon className="light-icon" icon="android-lock" />
      </Tooltip>
    );
  }

  return (
    <Link className="link" to={`/events/${event.id}/survey`}>
      <StarRatingAverage value={eventAnalytics.rating} />
    </Link>
  );
};

const EventRow = ({ eventAnalytics }) => {
  const { event } = eventAnalytics;

  return (
    <tr>
      <td>
        <EventLink event={event} />
      </td>
      <td>
        <Time time={eventAnalytics.startsAt} format="%b %e %Y, %l:%M %p" />
      </td>
      <td>
        <Time time={eventAnalytics.endsAt} format="%b %e %Y, %l:%M %p" />
      </td>
      <td>
        {eventAnalytics.commentCount}
      </td>
      <td>
        {eventAnalytics.participantCount}
      </td>
      <td>
        <EventRowRating eventAnalytics={eventAnalytics} />
      </td>
      <td>
        {eventAnalytics.expenseTotal}
      </td>
      <td>
        <Link className="action link" to={`/events/${event.id}/edit`}>
          Edit
        </Link>
      </td>
    </tr>
  );
};

const EventTable = ({ events }) => (
  <Table>
    <thead>
      <tr>
        <th className="max-width-300">Name</th>
        <th>Start Date</th>
        <th>End Date</th>
        <th>Comments</th>
        <th>Participants</th>
        <th>Rating</th>
        <th>Expenses</th>
        <th className="pull-right">Actions</th>
      </tr>
    </thead>
    <tbody>
      {events && events.map(eventAnalytics => (
        <EventRow key={eventAnalytics.event.id} eventAnalytics={eventAnalytics} />
      ))}
    </tbody>
  </Table>
);

const Events = () => {
  const [page, onPageClick] = usePagination();
  const { fetching, json: { events = [], pagination } } = useFetch(
    () => client.listEventAnalytics({ page }),
    [page]
  );

  return (
    <Panel limitWidth>
      <Panel.Heading primary>
        <Trans>Events</Trans>
      </Panel.Heading>
      <Panel.Body>
        <PageHeader title={t("Review Events")}>
          <Trans>
            Below is a list of all the events created at your organization.
            You can quickly see how much money was spent vs the average rating
            given by participants.
          </Trans>
        </PageHeader>
        <ExportEvents />
        <Loader loading={fetching}>
          <EventTable events={events} />

          {events.length < 1 && (
            <Warning>
              <strong>Uh oh!</strong> No results to show.
            </Warning>
          )}

          <Pagination {...pagination} onClick={onPageClick} />
        </Loader>
      </Panel.Body>
    </Panel>
  );
};

export default Events;
